import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { WEB_MAX_CONTENT_WIDTH } from '../../../theme/webLayoutWidths';

import {
  FlatList,
  Platform,
  Pressable,
  StyleSheet,
  View,
} from 'react-native';
import { GTFonts, useTheme } from '../../../theme';
import { useAppDispatch, useAppSelector } from '../../../redux/helper';
import useBackofficeAxios from '../../../hooks/useBackofficeAxios';
import useBackofficeOwnerAuth from '../../../hooks/useBackofficeOwnerAuth';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import type { RootStackParamList } from '../../../navigation/AppStack';
import Typography from '../../../components/Reusables/Typography';
import {
  formatWithCommas,
  replaceCurrencyString,
  fs,
  s,
  v,
} from '../../../utils/libs';
import {
  addPendingOrders,
  clearPending,
  setOrdersError,
  setOrdersLoading,
  setTableSearchResults,
  setTableSearchPagination,
} from '../../../redux/backoffice/ordersSlice';
import {
  getPendingOrders,
  searchOrderByReceiptId,
} from '../../../api/backofficeOrders';
import { useBackOfficeOrdersFilter } from '../../../Context/BackOfficeOrdersFilterContext';
import AppSkeleton from '../../../components/Reusables/AppSkeleton';
import { formatDisplayDateTime } from '../../../utils/formatDisplayDateTime';

type BackOfficePendingOrdersProps = {
  refreshVersion?: number;
};

const BackOfficePendingOrders: React.FC<BackOfficePendingOrdersProps> = ({
  refreshVersion = 0,
}) => {
  const { theme } = useTheme();
  const styles = createStyles(theme);
  const pending = useAppSelector(s => s.backofficeOrders.pending);
  const tableSearch = useAppSelector(s => s.backofficeOrders.tableSearch);
  const tableSearchTerm = useAppSelector(
    s => s.backofficeOrders.tableSearchTerm,
  );
  const employee = useAppSelector(
    s => s.auth.inApp.account.employeeInfo as any,
  );
  const ownerId = String(employee?.bp_owner_id || '');
  const locationVersion = useAppSelector(s => s.location.locationVersion);
  const axiosBackoffice = useBackofficeAxios();
  const { owner } = useBackofficeOwnerAuth();
  const dispatch = useAppDispatch();
  const nav = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const { startISO, endISO, dateFilterActive } = useBackOfficeOrdersFilter();
  const [refreshing, setRefreshing] = useState(false);
  const [localLoading, setLocalLoading] = useState(false);
  const requestSeqRef = useRef(0);
  const currency = useAppSelector(
    state =>
      state.location.currentLocation?.bp_location_settings
        ?.bp_location_currency || 'KES',
  );
  const currencyText = useMemo(
    () => replaceCurrencyString('ksh', currency),
    [currency],
  );

  const limit = useMemo(
    () => Number(pending?.perPage || pending?.limit || 100),
    [pending?.perPage, pending?.limit],
  );

  const isSearching = useMemo(
    () => Boolean(String(tableSearchTerm || '').trim().length > 0),
    [tableSearchTerm],
  );

  const searchDocs = useMemo(
    () =>
      (tableSearch?.docs || []).filter(
        (o: any) =>
          String(o?.bp_order_master_order_status || '').toLowerCase() ===
          'pending',
      ),
    [tableSearch?.docs],
  );

  const defaultPage = useMemo(
    () => ({
      docs: [],
      hasNextPage: false,
      hasPrevPage: false,
      limit: 100,
      nextPage: 1,
      page: 1,
      perPage: 100,
      totalDocs: 0,
      totalPages: 0,
    }),
    [],
  );

  const safeTableSearch = (tableSearch as any) ?? (defaultPage as any);
  const safePending = (pending as any) ?? (defaultPage as any);

  const normalizePaginated = useCallback(
    (payload: any) => ({
      ...payload,
      perPage:
        typeof payload?.perPage === 'number'
          ? payload.perPage
          : Number(payload?.limit ?? 100),
      docs: Array.isArray(payload?.docs) ? payload.docs : [],
    }),
    [],
  );

  const normalizeSearchPaginated = useCallback(
    (payload: any, page: number) => ({
      docs: Array.isArray(payload?.docs)
        ? payload.docs
        : Array.isArray(payload?.data)
          ? payload.data
          : [],
      hasNextPage: Boolean(
        payload?.hasNextPage ??
          (payload?.page ?? page) < (payload?.totalPages ?? 0),
      ),
      hasPrevPage: Boolean(payload?.hasPrevPage ?? (payload?.page ?? page) > 1),
      limit: Number(payload?.limit ?? payload?.perPage ?? 100),
      nextPage: Number(
        payload?.nextPage ??
          ((payload?.page ?? page) < (payload?.totalPages ?? 0)
            ? (payload?.page ?? page) + 1
            : (payload?.page ?? page)),
      ),
      page: Number(payload?.page ?? page),
      perPage: Number(payload?.perPage ?? payload?.limit ?? 100),
      totalDocs: Number(payload?.totalDocs ?? payload?.total ?? 0),
      totalPages: Number(
        payload?.totalPages ??
          Math.max(
            1,
            Math.ceil(
              Number(payload?.totalDocs ?? payload?.total ?? 0) /
                Number(payload?.perPage ?? payload?.limit ?? 100),
            ),
          ),
      ),
    }),
    [],
  );

  const fetchSearchPage = useCallback(
    async (page: number) => {
      if (!owner?.accessToken || !ownerId || !tableSearchTerm || localLoading)
        return;
      const seq = ++requestSeqRef.current;
      try {
        setLocalLoading(true);
        dispatch(setOrdersLoading(true));
        dispatch(setOrdersError(null));
        const res = await searchOrderByReceiptId(
          axiosBackoffice as any,
          ownerId,
          page,
          String(tableSearchTerm),
          { accessToken: owner?.accessToken },
        );
        if (res?.status === 200 && seq === requestSeqRef.current) {
          const payloadRaw = (res as any)?.data?.data ?? (res as any)?.data;
          const normalized = normalizeSearchPaginated(payloadRaw, page);
          dispatch(setTableSearchResults(normalized as any));
          dispatch(
            setTableSearchPagination({
              page: normalized.page,
              perPage: normalized.perPage,
            }),
          );
        }
      } catch (e: any) {
        if (seq === requestSeqRef.current)
          dispatch(setOrdersError(e?.message || 'Failed to search orders'));
      } finally {
        if (seq === requestSeqRef.current) {
          setLocalLoading(false);
          dispatch(setOrdersLoading(false));
        }
      }
    },
    [
      owner?.accessToken,
      ownerId,
      tableSearchTerm,
      localLoading,
      axiosBackoffice,
      dispatch,
      normalizeSearchPaginated,
    ],
  );

  const fetchPage = useCallback(
    async (page: number, clearFirst: boolean = false) => {
      if (!owner?.accessToken || !ownerId || localLoading) return;
      const seq = ++requestSeqRef.current;
      try {
        setLocalLoading(true);
        dispatch(setOrdersLoading(true));
        dispatch(setOrdersError(null));
        if (clearFirst) dispatch(clearPending());
        const res = await getPendingOrders(
          axiosBackoffice as any,
          ownerId,
          page,
          limit,
          dateFilterActive ? startISO : undefined,
          dateFilterActive ? endISO : undefined,
          { accessToken: owner?.accessToken },
        );
        if (res?.status === 200 && seq === requestSeqRef.current) {
          const payload = normalizePaginated(res?.data?.data ?? res.data);
          dispatch(addPendingOrders(payload));
        }
      } catch (e: any) {
        if (seq === requestSeqRef.current)
          dispatch(setOrdersError(e?.message || 'Failed to load orders'));
      } finally {
        if (seq === requestSeqRef.current) {
          setLocalLoading(false);
          dispatch(setOrdersLoading(false));
        }
      }
    },
    [
      owner?.accessToken,
      ownerId,
      localLoading,
      axiosBackoffice,
      limit,
      startISO,
      endISO,
      dateFilterActive,
      dispatch,
      normalizePaginated,
    ],
  );

  useEffect(() => {
    if (isSearching) return;
    requestAnimationFrame(() => {
      fetchPage(1, true);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    startISO,
    endISO,
    dateFilterActive,
    isSearching,
    locationVersion,
    refreshVersion,
  ]);

  const onRefresh = useCallback(() => {
    setRefreshing(true);
    if (isSearching) {
      fetchSearchPage(1).finally(() => setRefreshing(false));
    } else {
      fetchPage(1, true).finally(() => setRefreshing(false));
    }
  }, [fetchPage, fetchSearchPage, isSearching]);

  const onEndReached = useCallback(() => {
    if (isSearching) {
      if (
        safeTableSearch.hasNextPage &&
        safeTableSearch.docs.length < safeTableSearch.totalDocs
      ) {
        fetchSearchPage(safeTableSearch.nextPage);
      }
      return;
    }
    if (
      safePending.hasNextPage &&
      safePending.docs.length < safePending.totalDocs
    ) {
      fetchPage(safePending.nextPage);
    }
  }, [
    isSearching,
    safeTableSearch.hasNextPage,
    safeTableSearch.docs.length,
    safeTableSearch.totalDocs,
    safeTableSearch.nextPage,
    fetchSearchPage,
    safePending.hasNextPage,
    safePending.docs.length,
    safePending.totalDocs,
    safePending.nextPage,
    fetchPage,
  ]);

  const handlePress = useCallback(
    (order: any) => {
      nav.navigate('BackOfficeOrderDetails', {
        orderId: String(order?.bp_order_id),
      });
    },
    [nav],
  );

  const renderItem = useCallback(
    ({ item: order, index }: { item: any; index: number }) => {
      if (!order) return null;
      const rowNumber = index + 1;
      return (
        <View style={styles.container}>
          <Pressable
            unstable_pressDelay={80}
            accessibilityLabel={`View Order ${rowNumber}`}
            accessibilityHint="Tap to view order details"
            style={({ pressed, hovered }: any) => [
              styles.section,
              Platform.OS === 'web' && styles.sectionWeb,
              Platform.OS === 'web' && hovered && styles.sectionHovered,
              pressed && { backgroundColor: theme.colors.textTertiary, opacity: 0.2 },
            ]}
            onPress={() => handlePress(order)}>
            <View style={styles.rowWithNumber}>
              <View style={styles.rowNumberColumn}>
                <Typography style={styles.rowNumberText}>{rowNumber}</Typography>
              </View>
              <View style={styles.rowContent}>
            <View style={styles.top}>
              <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                <Typography style={styles.name}>
                  {order?.bp_order_customer_information?.length > 16
                    ? `${order.bp_order_customer_information.substring(0, 16)}...`
                    : order.bp_order_customer_information}
                </Typography>
                <Typography style={[styles.productCount, { marginLeft: s(4) }]}>
                  {'\u2022'}
                  {order.bp_sales_products_information?.length}
                  {'\u00A0'}
                  {order.bp_sales_products_information?.length > 1
                    ? 'Items'
                    : 'Item'}
                </Typography>
              </View>
              <Typography style={[styles.text]}>
                Total:{'\u00A0'}
                <Typography
                  style={[styles.text, { fontFamily: GTFonts.semibold }]}>
                  {currencyText}
                  {'\u00A0'}
                  {formatWithCommas(
                    order?.bp_order_master_payment_information
                      ?.bp_payment_amount,
                  )}
                </Typography>
              </Typography>
            </View>
            <View style={styles.bottom}>
              <Typography style={[styles.text, { fontSize: fs(14) }]}>
                {formatDisplayDateTime(order.bp_order_master_updated_at, '', {
                  relativeDayLabels: true,
                })}
              </Typography>
              <Typography style={styles.text}>
                {order.bp_order_master_receipt_reference_id ? (
                  <>
                    Receipt Id:{'\u00A0'}
                    <Typography
                      style={[styles.text, { fontFamily: GTFonts.semibold }]}>
                      {order.bp_order_master_receipt_reference_id}
                    </Typography>
                  </>
                ) : null}
              </Typography>
            </View>
              </View>
            </View>
          </Pressable>
        </View>
      );
    },
    [handlePress, styles, theme.colors.textTertiary],
  );

  const keyExtractor = useCallback(
    (item: any) => String(item?.bp_order_id),
    [],
  );
  const listData = isSearching ? searchDocs : pending.docs;
  const showInitialSkeleton = localLoading && listData.length === 0;
  const showFooterSkeleton = localLoading && listData.length > 0;

  return (
    <View
      style={[
        { flex: 1, backgroundColor: theme.colors.surface },
        Platform.OS === 'web' && {
          maxWidth: WEB_MAX_CONTENT_WIDTH,
          alignSelf: 'center',
          width: '100%',
        },
      ]}>
      <FlatList
        data={listData}
        keyExtractor={keyExtractor}
        initialNumToRender={10}
        refreshing={refreshing}
        onEndReachedThreshold={0.8}
        onEndReached={onEndReached}
        onRefresh={onRefresh}
        renderItem={renderItem}
        ListHeaderComponent={
          showInitialSkeleton ? (
            <View style={styles.skeletonWrap}>
              <AppSkeleton mode="orders_list" count={6} />
            </View>
          ) : null
        }
        ListFooterComponent={
          showFooterSkeleton ? (
            <View style={styles.skeletonWrap}>
              <AppSkeleton mode="orders_list" count={2} />
            </View>
          ) : null
        }
        showsVerticalScrollIndicator={false}
      />
    </View>
  );
};

export default BackOfficePendingOrders;

const createStyles = (theme: any) =>
  StyleSheet.create({
  container: {},
  skeletonWrap: {
    paddingVertical: v(10),
    paddingHorizontal: s(4),
  },
  rowWithNumber: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  rowNumberColumn: {
    minWidth: s(24),
    marginRight: s(8),
    alignItems: 'center',
    justifyContent: 'center',
  },
  rowNumberText: {
    fontSize: Platform.OS === 'web' ? fs(15) : fs(13),
    lineHeight: Platform.OS === 'web' ? fs(18) : fs(16),
    fontFamily: GTFonts.semibold,
    color: theme.colors.text,
    textAlign: 'center',
  },
  rowContent: {
    flex: 1,
  },
  section: {
    borderColor: theme.colors.shadowLight,
    borderBottomWidth: 1,
    padding: s(5),
    paddingTop: v(5),
  },
  sectionWeb: {
    ...(Platform.OS === 'web'
      ? ({
          cursor: 'pointer',
          transition: 'background-color 0.15s ease-out',
          borderRadius: 4,
          paddingHorizontal: s(8),
          marginHorizontal: s(-8),
        } as any)
      : {}),
  },
  sectionHovered: {
    backgroundColor: 'rgba(0,0,0,0.04)',
  },
  top: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingRight: s(2),
  },
  productCount: {
    fontFamily: GTFonts.regular,
    color: theme.colors.shadow,
    fontSize: fs(14),
    lineHeight: fs(20),
  },
  bottom: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 10,
    paddingRight: s(2),
  },
  name: {
    marginVertical: v(1),
    fontFamily: GTFonts.semibold,
    fontSize: fs(16),
    lineHeight: fs(18),
  },
  text: {
    lineHeight: 20,
    fontFamily: GTFonts.regular,
    fontSize: fs(14),
    color: theme.colors.graphiteDark,
  },
});
